import { api, ErrorApi } from '../api';

/**
 * Bajar el PDF de un comprobante (KAN-147), desde
 * `GET /api/v1/comprobantes/{id}/pdf` (KAN-149).
 *
 * # POR QUÉ NO ALCANZA CON UN `<a href>` NI UN `<iframe>`
 *
 * El endpoint exige `Authorization: Bearer`, y **el navegador no manda ese
 * header** cuando sigue un enlace o carga un `iframe`: la sesión de este front
 * vive sólo en memoria (KAN-142), no hay cookie que viaje sola. Un enlace
 * directo al endpoint daría `401` siempre.
 *
 * La salida NO es meter el token en la query string. Ahí queda en el historial
 * del navegador, en los logs de cualquier proxy y en el `Referer` de lo
 * próximo que se abra — y con ese token se emiten comprobantes fiscales. Lo
 * correcto es lo de acá: pedirlo con el cliente HTTP, que ya pone la
 * credencial, y quedarse con los bytes.
 */

/** Un PDF ya en memoria, con el nombre que le puso el backend. */
export interface PdfDescargado {
  readonly blob: Blob;
  /**
   * El nombre que mandó el backend (`Factura-A-00001-00000015.pdf`). **No se
   * arma uno propio**: un archivo que no dice qué comprobante es se vuelve
   * inencontrable en la carpeta de descargas de cualquiera, y además el backend
   * ya lo genera igual para todos sus consumidores.
   */
  readonly nombre: string;
}

export async function obtenerPdf(id: number): Promise<PdfDescargado> {
  let resultado;
  try {
    // `parseAs: 'blob'` porque la respuesta es binaria: sin esto el cliente
    // intentaría leerla como JSON y rompería con un PDF perfectamente válido.
    resultado = await api.GET('/api/v1/comprobantes/{id}/pdf', {
      params: { path: { id } },
      parseAs: 'blob',
    });
  } catch (causa) {
    throw ErrorApi.deRed(causa);
  }

  if (!resultado.response.ok) {
    // El cuerpo de error SÍ es JSON (`{error, detalle}` de `ManejadorDeErrores`),
    // aunque el camino feliz sea binario. Se lo lee como texto y se parsea a
    // mano porque `parseAs: 'blob'` ya se llevó el cuerpo a un Blob.
    throw ErrorApi.deRespuesta(resultado.response.status, await cuerpoDeError(resultado));
  }

  return {
    blob: resultado.data as Blob,
    nombre: nombreDeArchivo(resultado.response.headers.get('Content-Disposition')),
  };
}

/**
 * Saca el `filename` del `Content-Disposition`.
 *
 * Si el header no viene o no se entiende se usa un nombre genérico en vez de
 * fallar: **el PDF ya está bajado y es correcto**, y negarle el archivo a
 * alguien porque un header no matcheó una expresión regular sería perder lo
 * importante por lo accesorio.
 */
export function nombreDeArchivo(contentDisposition: string | null): string {
  const encontrado = /filename="?([^";]+)"?/i.exec(contentDisposition ?? '');
  return encontrado?.[1]?.trim() || 'comprobante.pdf';
}

async function cuerpoDeError(resultado: { data?: unknown; error?: unknown }): Promise<unknown> {
  // Según el estado, openapi-fetch deja el cuerpo en `error` o en `data`.
  const cuerpo = resultado.error ?? resultado.data;
  if (cuerpo instanceof Blob) {
    try {
      return JSON.parse(await cuerpo.text());
    } catch {
      // Un error sin cuerpo del contrato: que `ErrorApi` decida por el estado.
      return undefined;
    }
  }
  return cuerpo;
}
